import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useConfig } from '../config.js';
import { permissionNote, currentPushContext } from '../push-help.js';
import { track } from '../events.js';
import { useConfirm } from './ConfirmDialog.jsx';

// Account-level settings, mounted in the tank menu (TankSheet) — the
// only account-scoped surface in the app. Three jobs:
//   1. Show the user their current email.
//   2. Let them set OR change their password (reuses /api/auth/set-password).
//   3. Start an email-change flow: post the new address, a confirmation
//      link goes to it, the user clicks → /auth/verify-email-change
//      updates users.email and re-signs them in on this device.
//
// Email is stored read-only here; the actual swap happens server-side
// after the new-address mailbox click. We tell the user that.

export function AccountPanel() {
  const [me, setMe] = useState(null); // null = loading
  const [section, setSection] = useState(null); // null | 'email' | 'password'

  const reload = async () => {
    try {
      const r = await api('/api/auth/me');
      setMe(r.user || null);
    } catch {
      setMe(null);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (me === null) {
    return (
      <div className="ts-add-form" style={{ marginTop: 12 }}>
        <p className="ts-hint">Loading account…</p>
      </div>
    );
  }

  return (
    <div className="ts-add-form" style={{ marginTop: 12 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">Account</h4>
      </div>

      <p className="ts-hint" style={{ marginTop: 6 }}>
        Signed in as <strong>{me.email}</strong>
      </p>

      {section === 'email' ? (
        <EmailChangeForm currentEmail={me.email} onDone={() => setSection(null)} />
      ) : section === 'password' ? (
        <PasswordForm
          hasPassword={!!me.has_password}
          onDone={async () => {
            setSection(null);
            await reload();
          }}
        />
      ) : (
        <>
          <div className="ts-row-body" style={{ marginTop: 8 }}>
            <button type="button" className="ts-chip" onClick={() => setSection('email')}>
              Change email
            </button>
            <button type="button" className="ts-chip" onClick={() => setSection('password')}>
              {me.has_password ? 'Change password' : 'Set a password'}
            </button>
          </div>
          <div id="account-notifications" style={{ scrollMarginTop: 12 }}>
            <EmailPreferencesRow me={me} onChanged={reload} />
          </div>
          <RetailerPreferenceRow me={me} onChanged={reload} />
          <div id="account-siri" style={{ scrollMarginTop: 12 }}>
            <ShortcutTokensRow />
          </div>
        </>
      )}
    </div>
  );
}

// Siri Shortcuts credentials. Minted here, in a logged-in browser —
// never by another shortcut — so a stolen token cannot mint successors.
function ShortcutTokensRow() {
  // Read the origin rather than hardcoding aquasensei.app: the same
  // panel has to give the right endpoint on staging and on localhost.
  const base = typeof window === 'undefined' ? '' : window.location.origin;
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [fresh, setFresh] = useState(null); // plaintext, this render only
  const [busy, setBusy] = useState(false);
  const [tanks, setTanks] = useState([]);

  const load = async () => {
    try {
      setRows(await api('/api/shortcut-tokens'));
    } catch {
      setRows([]);
    }
  };

  // With more than one tank a shortcut must name its tank_id, and this is
  // the only place in the app that shows one.
  useEffect(() => {
    if (!open) return;
    api('/api/tanks')
      .then((list) => setTanks(Array.isArray(list) ? list : []))
      .catch(() => setTanks([]));
  }, [open]);

  useEffect(() => {
    if (open && rows === null) load();
  }, [open, rows]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api('/api/shortcut-tokens', {
        method: 'POST',
        body: { name: 'Siri Shortcut' },
      });
      // Shown once. The server keeps only a sha256, so there is no
      // second chance to read it and the copy has to say so.
      setFresh(r.token);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    await api(`/api/shortcut-tokens/${id}`, { method: 'DELETE' }).catch(() => {});
    await load();
  };

  const live = (rows || []).filter((r) => !r.revoked_at);

  return (
    <div style={{ marginTop: 14 }}>
      <button type="button" className="ts-chip" onClick={() => setOpen((v) => !v)}>
        Siri Shortcuts {live.length ? `(${live.length})` : ''}
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          <p className="ts-hint">
            Log a reading or ask Sensei to evaluate your tank by voice. Siri reads back what it
            heard before anything is saved.
          </p>
          <p className="ts-hint" style={{ marginTop: 4 }}>
            Create a token, then follow the five-minute setup in{' '}
            <a href="/siri" target="_blank" rel="noreferrer" style={{ color: 'var(--cyan)' }}>
              the Shortcuts guide
            </a>
            . Point each action at <code className="font-mono">{base}/api/shortcuts/…</code>
          </p>
          {fresh && (
            <div className="ts-add-form" style={{ marginTop: 8 }}>
              <p className="ts-hint" style={{ margin: 0 }}>
                Copy this now — it is shown once and cannot be recovered.
              </p>
              <code
                className="font-mono"
                style={{ display: 'block', wordBreak: 'break-all', marginTop: 6, fontSize: 14 }}
              >
                {fresh}
              </code>
            </div>
          )}
          <div className="ts-row-body" style={{ marginTop: 8 }}>
            <button type="button" className="ts-save" onClick={create} disabled={busy}>
              {busy ? '…' : 'Create a token'}
            </button>
          </div>
          {tanks.length > 1 && (
            <div style={{ marginTop: 8 }}>
              <p className="ts-hint" style={{ margin: 0 }}>
                You have {tanks.length} tanks, so each shortcut needs a <code>tank_id</code>:
              </p>
              {tanks.map((t) => (
                <p key={t.id} className="ts-hint" style={{ margin: '4px 0 0' }}>
                  {t.name || 'Tank'}:{' '}
                  <code className="font-mono" style={{ wordBreak: 'break-all', userSelect: 'all' }}>
                    {t.id}
                  </code>
                </p>
              ))}
            </div>
          )}
          {live.map((t) => (
            <div key={t.id} className="ts-row-body" style={{ marginTop: 6, alignItems: 'center' }}>
              <span className="ts-hint" style={{ flex: 1 }}>
                {t.name || 'Shortcut'} ·{' '}
                {t.last_used_at
                  ? `used ${new Date(t.last_used_at).toLocaleDateString()}`
                  : 'never used'}
              </span>
              <button type="button" className="ts-chip" onClick={() => revoke(t.id)}>
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Phase 3 — email channel preferences. Preset chip row up top for
// the 80% case (All / Important / Critical only / Off); Fine-tune
// accordion below for power users. Both edit the same email_prefs
// JSONB blob; PATCH always sends the resolved full shape so the
// JSONB column stays atomic.
const PICKER_STYLE = {
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--surface-border)',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 16,
  minHeight: 44,
};

const DEFAULT_CHANNELS = { email: true, webpush: true, sms: false };
// Email carries only what needs the keeper unless they ask for more
// (mirrors server/notifications/prefs.js).
const NO_EXTRAS = { mild_alerts: false, tasks: false };
const ALL_EXTRAS = { mild_alerts: true, tasks: true };
const DEFAULT_PREFS = {
  digest: false,
  alerts: { enabled: true, severity: 'any' },
  tasks: true,
  supply_low: true,
  device_down: true,
  power_loss: true,
  photo_read: false,
  channels: { ...DEFAULT_CHANNELS },
  email_extras: { ...NO_EXTRAS },
};
const PRESETS = {
  all: {
    digest: true,
    alerts: { enabled: true, severity: 'any' },
    tasks: true,
    supply_low: true,
    device_down: true,
    power_loss: true,
    photo_read: true,
    channels: { email: true, webpush: true, sms: false },
    email_extras: { ...ALL_EXTRAS },
  },
  important: {
    digest: false,
    alerts: { enabled: true, severity: 'any' },
    tasks: true,
    supply_low: true,
    device_down: true,
    power_loss: true,
    photo_read: false,
    channels: { email: true, webpush: true, sms: false },
    email_extras: { ...NO_EXTRAS },
  },
  critical: {
    digest: false,
    alerts: { enabled: true, severity: 'severe' },
    tasks: false,
    supply_low: false,
    device_down: true,
    power_loss: true,
    photo_read: false,
    channels: { email: true, webpush: true, sms: false },
    email_extras: { ...NO_EXTRAS },
  },
  // Mirrors server/notifications/prefs.js: Off keeps the power-loss page,
  // and email and push to carry it (owner, 2026-10-01).
  off: {
    digest: false,
    alerts: { enabled: false, severity: 'any' },
    tasks: false,
    supply_low: false,
    device_down: false,
    power_loss: true,
    photo_read: false,
    channels: { email: true, webpush: true, sms: false },
    email_extras: { ...NO_EXTRAS },
  },
};
const PRESET_CHIPS = [
  { k: 'all', label: 'All' },
  { k: 'important', label: 'Important' },
  { k: 'critical', label: 'Critical only' },
  { k: 'off', label: 'Off' },
];

function shapesEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function applyDefaults(blob) {
  const b = blob && typeof blob === 'object' ? blob : {};
  const alerts = b.alerts && typeof b.alerts === 'object' ? b.alerts : {};
  const ch = b.channels && typeof b.channels === 'object' ? b.channels : {};
  const ex = b.email_extras && typeof b.email_extras === 'object' ? b.email_extras : {};
  return {
    digest: b.digest === true,
    alerts: {
      enabled: alerts.enabled !== false,
      severity: alerts.severity === 'severe' ? 'severe' : 'any',
    },
    tasks: b.tasks !== false,
    supply_low: b.supply_low !== false,
    device_down: b.device_down !== false,
    power_loss: b.power_loss === undefined ? b.device_down !== false : b.power_loss !== false,
    photo_read: b.photo_read === true,
    // Mirrors the server shape: without this, any prefs save from the
    // panel would silently drop (= disable) the weekly test alert.
    heartbeat: {
      enabled: b.heartbeat?.enabled === true,
      weekday: Number.isInteger(b.heartbeat?.weekday) ? b.heartbeat.weekday : 1,
      hour: Number.isInteger(b.heartbeat?.hour) ? b.heartbeat.hour : 9,
    },
    channels: {
      email: ch.email !== false,
      webpush: ch.webpush !== false,
      sms: ch.sms === true,
    },
    // Kinds stopped by email only (an unsubscribe link or reply). Carried
    // through every save, or touching any switch here would quietly start
    // those emails again.
    email_mute: Object.fromEntries(
      Object.keys(EMAIL_MUTE_LABEL)
        .filter((k) => b.email_mute?.[k] === true)
        .map((k) => [k, true])
    ),
    email_extras: {
      mild_alerts: ex.mild_alerts === true,
      tasks: ex.tasks === true,
    },
  };
}

const EMAIL_MUTE_LABEL = {
  alerts: 'off-band alerts',
  mild_alerts: 'mild alerts (severe ones still come)',
  tasks: 'task reminders',
  supply_low: 'running-low reminders',
  device_down: 'device alerts',
  photo_read: 'photo reads',
  editor_action: 'shared-tank change notices',
};

const POWER_LOSS_LABEL = 'Power loss (a display goes offline)';

function EmailPreferencesRow({ me, onChanged }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(false);
  const prefs = applyDefaults(me.email_prefs || DEFAULT_PREFS);

  // Any save here is the keeper's own choice, so the one-time "we email
  // less now" note on Home has done its job.
  const save = async (next, preset) => {
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/me', {
        method: 'PATCH',
        body: { email_prefs: { ...next, email_v2_notice: false }, ...(preset ? { preset } : {}) },
      });
      await onChanged();
      return true;
    } catch (e) {
      setErr(e.message || 'Could not save.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Match the current prefs against a preset so the active chip
  // gets highlighted. "Custom" if none match — the user has
  // hand-tuned, no chip lit.
  // Presets predate the heartbeat field; strip it for the comparison so
  // enabling the weekly test does not flip every chip to "Custom".
  const matchedPreset = Object.entries(PRESETS).find(([, shape]) =>
    shapesEqual(shape, { ...prefs, heartbeat: undefined, email_mute: undefined })
  )?.[0];

  const setChannel = (patch) => save({ ...prefs, ...patch });
  const setExtra = (k, v) => save({ ...prefs, email_extras: { ...prefs.email_extras, [k]: v } });
  const setProductNews = async (on) => {
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/me', { method: 'PATCH', body: { product_news: on } });
      await onChanged();
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };
  const setAlertsField = (field, value) =>
    save({ ...prefs, alerts: { ...prefs.alerts, [field]: value } });

  return (
    <div style={{ marginTop: 14 }}>
      <div
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
          marginBottom: 6,
        }}
      >
        Notifications
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {PRESET_CHIPS.map((p) => (
          <button
            key={p.k}
            type="button"
            disabled={busy}
            onClick={async () => {
              if (p.k === 'off') {
                const ok = await confirm({
                  title: 'Turn off notifications?',
                  body: `Alerts, task reminders, the morning email${prefs.heartbeat?.enabled === true ? ', the weekly test' : ''} and product news stop, by email, push and text. RSVP reminders for Near You events you said you are going to still come by push. One alert stays on: if a Printed Reef display stops reaching TankSensei for 15 minutes, you still get a push and an email, because it can mean the power is out and the heater and return pump are off. To stop that too, open Which alerts and turn off "${POWER_LOSS_LABEL}".`,
                  confirmLabel: 'Turn off',
                  destructive: true,
                });
                if (!ok) return;
              }
              track('email_prefs.preset', 'account', { preset: p.k });
              // Presets predate the heartbeat field; carry the user's
              // opted-in test through or the chip silently disables it.
              // Off stops it: the old Off had no channel to deliver it on,
              // and the new one keeps email and push for the power-loss page.
              const saved = await save(
                {
                  ...PRESETS[p.k],
                  heartbeat:
                    p.k === 'off' ? { ...prefs.heartbeat, enabled: false } : prefs.heartbeat,
                  email_mute: prefs.email_mute,
                },
                p.k
              );
              // Off used to switch the email channel off, which stopped
              // product news too; it keeps email on now for the power-loss
              // page, so it says no to the news itself.
              if (saved && p.k === 'off' && me.broadcast_opt_out === false) {
                await setProductNews(false);
              }
            }}
            className="ts-chip"
            style={{
              background: matchedPreset === p.k ? 'var(--cyan)' : 'transparent',
              color: matchedPreset === p.k ? 'var(--cyan-text)' : 'var(--text-muted)',
              borderColor: matchedPreset === p.k ? 'var(--cyan)' : 'var(--surface-border)',
            }}
          >
            {p.label}
          </button>
        ))}
        {!matchedPreset && (
          // Read-only Custom chip lights up when no preset matches —
          // confirms the user IS on a custom mix instead of hiding it.
          // Tapping any preset above clears the Custom state.
          <span
            className="ts-chip"
            aria-disabled="true"
            title="Your settings don't match a preset — fine-tuned below."
            style={{
              background: 'var(--violet, #5fd6c4)',
              color: 'var(--cyan-text)',
              borderColor: 'var(--violet, #5fd6c4)',
              cursor: 'default',
            }}
          >
            Custom
          </span>
        )}
      </div>
      {matchedPreset === 'off' && prefs.power_loss && (
        <div className="ts-hint" style={{ margin: '8px 0' }}>
          Everything is off except power-loss alerts and reminders for Near You events you said you
          are going to. If a Printed Reef display stops reaching TankSensei, you still get a push
          and an email; to stop those too, open Which alerts below and turn off {POWER_LOSS_LABEL}.
        </div>
      )}
      {Object.keys(prefs.email_mute).length > 0 && (
        <div className="ts-hint" style={{ margin: '8px 0' }}>
          You stopped these by email:{' '}
          {Object.keys(prefs.email_mute)
            .map((k) => EMAIL_MUTE_LABEL[k])
            .join(', ')}
          . Push and texts are not affected.{' '}
          <button
            type="button"
            className="ts-chip"
            disabled={busy}
            style={{ minHeight: 44 }}
            onClick={() => save({ ...prefs, email_mute: {} })}
          >
            Email these again
          </button>
        </div>
      )}
      <div style={{ margin: '10px 0 4px' }}>
        <div style={{ fontSize: 15, color: 'var(--text)' }}>Emails</div>
        <div className="ts-hint">
          Emailed while their switches under Which alerts are on: a reading far out of range, a
          display or probe gone quiet, and, on a tank you own, when someone you share it with
          deletes readings, notes, doses, equipment or a quarantine log, removes or brings back a
          fish or coral, or imports readings.
        </div>
        <div className="ts-hint" style={{ marginTop: 6 }}>
          Also email me:
        </div>
        <Toggle
          checked={prefs.digest}
          disabled={busy}
          label="Morning summary"
          hint="On mornings with something to report: what's off, today's tasks, what's running low."
          onChange={(v) => setChannel({ digest: v })}
        />
        <Toggle
          checked={prefs.email_extras.mild_alerts}
          disabled={busy || !prefs.alerts.enabled || prefs.alerts.severity === 'severe'}
          label="Readings slightly out of range"
          hint="The app and push show these either way. If push can't reach you, they come by email."
          onChange={(v) => setExtra('mild_alerts', v)}
        />
        <Toggle
          checked={prefs.email_extras.tasks}
          disabled={busy || !prefs.tasks}
          label="Task reminders"
          hint="When a task comes due. Not sent while you get the morning summary, which lists your tasks."
          onChange={(v) => setExtra('tasks', v)}
        />
        <Toggle
          checked={me.broadcast_opt_out === false}
          disabled={busy}
          label="Product news"
          hint="New features and updates from the TankSensei team."
          onChange={setProductNews}
        />
      </div>
      {/* How alerts reach you — the delivery channels. Email is the
          baseline; Web Push needs browser permission; SMS needs a
          verified phone + consent. */}
      <DeliveryChannels prefs={prefs} save={save} busy={busy} />

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="ts-chip"
        style={{ marginTop: 10, width: '100%', textAlign: 'left' }}
      >
        {expanded ? '▾' : '▸'} Which alerts
      </button>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          <Toggle
            checked={prefs.alerts.enabled}
            disabled={busy}
            label="Off-band alerts"
            hint="Tells you when a reading goes out of your range. While it stays out, push reminds you once a day if it is slightly out and every 6 hours if it is far out."
            onChange={(v) => setAlertsField('enabled', v)}
          />
          {prefs.alerts.enabled && (
            <div style={{ paddingLeft: 26, marginTop: -4, marginBottom: 6 }}>
              <label
                className="ts-hint"
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              >
                <span>Send</span>
                <select
                  value={prefs.alerts.severity}
                  disabled={busy}
                  onChange={(e) => setAlertsField('severity', e.target.value)}
                  style={PICKER_STYLE}
                >
                  <option value="any">any off-band crossing</option>
                  <option value="severe">only severe ones</option>
                </select>
              </label>
            </div>
          )}
          <Toggle
            checked={prefs.tasks}
            disabled={busy}
            label="Task reminders"
            hint="A push when a task comes due — water change, test, cartridge swap — and the task email too, if you chose it above. Never a text."
            onChange={(v) => setChannel({ tasks: v })}
          />
          <Toggle
            checked={prefs.power_loss}
            disabled={busy}
            label={POWER_LOSS_LABEL}
            hint="When a Printed Reef display stops reaching TankSensei for 15 minutes. It can mean a power cut, so it comes by push (and text, if on) even at night, plus one email, and one note when the display is back."
            onChange={async (v) => {
              if (!v) {
                const ok = await confirm({
                  title: 'Turn off power-loss alerts?',
                  body: 'If a display stops reaching TankSensei, for example because the power is out, nobody will tell you. The heater and return pump may be off too.',
                  confirmLabel: 'Turn off',
                  destructive: true,
                });
                if (!ok) return;
              }
              setChannel({ power_loss: v });
            }}
          />
          <Toggle
            checked={prefs.device_down}
            disabled={busy}
            label="Probes and dose heads"
            hint="When an Apex or HYDROS probe goes quiet, two controllers report the same test, or a dose head stops dosing. Test-kit readings are never in here."
            onChange={(v) => setChannel({ device_down: v })}
          />
        </div>
      )}
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
    </div>
  );
}

// Delivery channels — WHICH notification types carry your alerts.
// Email is the baseline. Web Push registers a browser subscription
// (needs permission + server VAPID keys). SMS needs a phone + consent.
// The channel switches live in the same prefs blob; the backend skips
// any channel turned off.
function DeliveryChannels({ prefs, save, busy }) {
  const channels = prefs.channels || { email: true, webpush: true, sms: false };
  // Texts stay hidden until the admin `sms` setting is switched on (the
  // carrier registration is pending); a stored opt-in is kept meanwhile.
  const smsOn = useConfig()?.sms?.enabled === true;
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNote, setPushNote] = useState('');
  const [pushNoteOk, setPushNoteOk] = useState(false);
  const [smsOpen, setSmsOpen] = useState(channels.sms);
  const [phone, setPhone] = useState('');
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsNote, setSmsNote] = useState('');
  // Whether THIS browser gets push: a live subscription, permission, and
  // not switched off here. The account flag alone said nothing about the
  // device the keeper is holding.
  const [deviceOn, setDeviceOn] = useState(null);
  // Switched off on this device by the keeper, or no push here at all: a
  // test or a refresh would only re-subscribe what they just turned off.
  const [pushOffHere, setPushOffHere] = useState(false);

  const setChannelFlag = (channel, value) =>
    save({ ...prefs, channels: { ...channels, [channel]: value } });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { pushSupported, getCurrentPushSubscription, pushOffOnThisDevice } =
          await import('../push.js');
        if (!pushSupported() || pushOffOnThisDevice()) {
          if (alive) {
            setDeviceOn(false);
            setPushOffHere(true);
          }
          return;
        }
        const sub = await getCurrentPushSubscription();
        const granted =
          typeof Notification !== 'undefined' && Notification.permission === 'granted';
        if (alive) setDeviceOn(!!sub && granted);
      } catch {
        if (alive) setDeviceOn(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load the saved SMS contact so the field reflects reality.
  useEffect(() => {
    let alive = true;
    api('/api/notifications/sms')
      .then((r) => {
        if (!alive) return;
        if (r.phone) setPhone(r.phone);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Map an enableWebPush() failure to a friendly explanation.
  const pushFailNote = (reason) =>
    permissionNote(reason, currentPushContext()) ||
    (reason === 'unsupported'
      ? "This browser doesn't support web push (try Chrome, Edge, or Firefox; iOS needs the app added to the Home Screen)."
      : reason === 'not_configured'
        ? 'Saved — push delivery turns on once the server has its push keys configured.'
        : 'Saved, but the browser subscription failed. Try again from a supported browser.');

  // Off unsubscribes this browser only; the keeper's other devices keep
  // their alerts. On also switches push back on for the account if it was
  // off (an older version of this switch, or the Off preset).
  const toggleWebPush = async (on) => {
    setPushNote('');
    if (!on) {
      setPushBusy(true);
      try {
        const { disableWebPush } = await import('../push.js');
        await disableWebPush({ remember: true });
        setDeviceOn(false);
        setPushOffHere(true);
        setPushNoteOk(true);
        setPushNote('Push is off on this device. Your other devices still get alerts.');
      } finally {
        setPushBusy(false);
      }
      return;
    }
    setPushBusy(true);
    try {
      const { enableWebPush } = await import('../push.js');
      await enableWebPush();
      setDeviceOn(true);
      setPushOffHere(false);
      if (!channels.webpush) setChannelFlag('webpush', true);
      setPushNoteOk(true);
      setPushNote('Push is on for this device.');
    } catch (e) {
      // Save the PREFERENCE even when the browser can't subscribe yet —
      // it'll subscribe next time the prerequisite is met. Tell the
      // user what's missing.
      if (!channels.webpush) setChannelFlag('webpush', true);
      setPushNoteOk(false);
      setPushNote(pushFailNote(e?.reason));
    } finally {
      setPushBusy(false);
    }
  };

  // Self-heal on load: if the saved preference says push is ON but this
  // browser has no live subscription, silently re-subscribe. This is the
  // exact state that leaves a keeper getting nothing — they toggled push
  // on before the server had VAPID keys (subscription failed, preference
  // saved), and it never retried. Now that keys are set, one quiet
  // re-subscribe fixes it without them touching a thing.
  useEffect(() => {
    if (!channels.webpush) return;
    let alive = true;
    (async () => {
      try {
        const { pushSupported, getCurrentPushSubscription, enableWebPush, pushOffOnThisDevice } =
          await import('../push.js');
        // Switched off on this device by the keeper: leave it off.
        if (!pushSupported() || pushOffOnThisDevice()) return;
        const existing = await getCurrentPushSubscription();
        if (existing || !alive) return;
        // Permission may already be granted from a prior enable; if it's
        // in 'default' we skip (don't prompt unbidden on a settings open).
        if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') return;
        await enableWebPush();
        if (alive) setDeviceOn(true);
      } catch {
        /* best effort — the visible toggle/refresh still works */
      }
    })();
    return () => {
      alive = false;
    };
    // Run once on mount; channels.webpush is read at call time.
  }, []);

  // Send a real push to this device's subscriptions, bypassing the gate
  // and prefs — a pure "does delivery work" check that names the failure.
  const testPush = async () => {
    setPushNote('');
    setPushBusy(true);
    try {
      // Make sure this browser is actually subscribed first — the common
      // "nothing arrives" cause is no subscription at all.
      const { enableWebPush } = await import('../push.js');
      await enableWebPush()
        .then(() => setDeviceOn(true))
        .catch(() => {});
      const r = await api('/api/push/test', { method: 'POST' });
      if (r.ok) {
        setPushNoteOk(true);
        setPushNote('Sent — you should see it on this device within a few seconds.');
      } else {
        setPushNoteOk(false);
        setPushNote(
          r.reason === 'no_config'
            ? 'Server push keys are not configured yet.'
            : r.reason === 'no_subscription'
              ? "This browser isn't subscribed. Tap Refresh push, then test again."
              : 'The push service rejected it. Tap Refresh push to re-subscribe, then test again.'
        );
      }
    } catch {
      setPushNoteOk(false);
      setPushNote('Could not send a test. Try Refresh push, then test again.');
    } finally {
      setPushBusy(false);
    }
  };

  // One-tap re-subscribe — no need to toggle off then on (which could
  // strand the toggle if the old registration was broken). Forces a
  // fresh subscription and re-registers it server-side.
  const refreshPush = async () => {
    setPushNote('');
    setPushBusy(true);
    try {
      const { enableWebPush } = await import('../push.js');
      await enableWebPush({ force: true });
      setDeviceOn(true);
      if (!channels.webpush) setChannelFlag('webpush', true);
      setPushNoteOk(true);
      setPushNote('Push refreshed on this device.');
    } catch (e) {
      setPushNoteOk(false);
      setPushNote(pushFailNote(e?.reason));
    } finally {
      setPushBusy(false);
    }
  };

  const saveSms = async (on) => {
    setSmsNote('');
    if (!on) {
      setChannelFlag('sms', false);
      setSmsOpen(false);
      // Clear consent too (keeps the number off the send path).
      await api('/api/notifications/sms', { method: 'PUT', body: { phone, consent: false } }).catch(
        () => {}
      );
      return;
    }
    setSmsOpen(true);
  };

  const confirmSms = async () => {
    setSmsBusy(true);
    setSmsNote('');
    try {
      const r = await api('/api/notifications/sms', {
        method: 'PUT',
        body: { phone, consent: true },
      });
      if (r.consented) {
        setChannelFlag('sms', true);
        setSmsNote('Texts on. Reply STOP to any message to opt out.');
      } else {
        setSmsNote('Could not confirm — check the number.');
      }
    } catch (e) {
      setSmsNote(
        e?.payload?.error === 'invalid_phone' ? 'That phone number looks off.' : 'Could not save.'
      );
    } finally {
      setSmsBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
          marginBottom: 4,
        }}
      >
        How they reach you
      </div>
      <Toggle
        checked={channels.email}
        disabled={busy}
        label="Email"
        hint="Alert emails, and any you chose above, to your account address."
        onChange={(v) => setChannelFlag('email', v)}
      />
      <Toggle
        checked={channels.webpush && deviceOn === true}
        disabled={busy || pushBusy || deviceOn === null}
        label="Phone & browser alerts on this device"
        hint={
          channels.webpush
            ? "Pops up on this device even when TankSensei isn't open. Turning it off here leaves your other devices on. On iPhone, add TankSensei to your Home Screen first."
            : 'Push is switched off for every device on your account. Turning it on here turns it back on everywhere.'
        }
        onChange={toggleWebPush}
      />
      {!pushOffHere && (
        <div
          style={{
            paddingLeft: 26,
            marginTop: 2,
            marginBottom: 4,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="ts-chip"
            disabled={pushBusy}
            onClick={testPush}
            style={{ minHeight: 44 }}
          >
            {pushBusy ? 'Working…' : 'Send a test notification'}
          </button>
          <button
            type="button"
            className="ts-chip"
            disabled={pushBusy}
            onClick={refreshPush}
            style={{ minHeight: 44 }}
          >
            Refresh push
          </button>
        </div>
      )}
      <Toggle
        checked={prefs.heartbeat?.enabled === true}
        disabled={busy}
        label="Weekly test alert"
        hint="A clearly-labeled test push at your chosen time proves alerts still reach at least one of your phones or browsers. If it ever fails to deliver, you get an email instead - push subscriptions expire silently, and the first missed alert should be a drill, not an emergency."
        onChange={(v) =>
          save({
            ...prefs,
            heartbeat: { weekday: 1, hour: 9, ...(prefs.heartbeat || {}), enabled: v },
          })
        }
      />
      {prefs.heartbeat?.enabled === true && (
        <div
          style={{
            paddingLeft: 26,
            marginTop: 2,
            marginBottom: 6,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            fontSize: 14,
            color: 'var(--text-muted)',
          }}
        >
          <select
            value={prefs.heartbeat.weekday}
            disabled={busy}
            aria-label="Test alert weekday"
            onChange={(e) =>
              save({
                ...prefs,
                heartbeat: { ...prefs.heartbeat, weekday: Number(e.target.value) },
              })
            }
            style={PICKER_STYLE}
          >
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(
              (d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              )
            )}
          </select>
          <span>at</span>
          <select
            value={prefs.heartbeat.hour}
            disabled={busy}
            aria-label="Test alert hour"
            onChange={(e) =>
              save({
                ...prefs,
                heartbeat: { ...prefs.heartbeat, hour: Number(e.target.value) },
              })
            }
            style={PICKER_STYLE}
          >
            {/* 8am-8pm only: quiet hours (21:00-08:00) hold routine
                pushes, so a night-time test could structurally never
                deliver - don't offer a time that cannot work. */}
            {Array.from({ length: 13 }, (_, i) => i + 8).map((h) => (
              <option key={h} value={h}>
                {h === 12 ? '12pm' : h < 12 ? `${h}am` : `${h - 12}pm`}
              </option>
            ))}
          </select>
        </div>
      )}
      {pushNote && (
        <div
          className="ts-hint"
          style={{
            paddingLeft: 26,
            marginTop: -2,
            color: pushNoteOk ? 'var(--text-muted)' : 'var(--warn)',
          }}
        >
          {pushNote}
        </div>
      )}
      {smsOn && (
        <Toggle
          checked={channels.sms}
          disabled={busy || smsBusy}
          label="Text message (SMS)"
          hint="Critical alerts only: dangerous swings and a display going offline."
          onChange={saveSms}
        />
      )}
      {smsOn && smsOpen && !channels.sms && (
        <div style={{ paddingLeft: 26, marginTop: -2, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="tel"
              value={phone}
              placeholder="+1 555 123 4567"
              onChange={(e) => setPhone(e.target.value)}
              style={{ flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              className="ts-chip"
              disabled={smsBusy || !phone.trim()}
              onClick={confirmSms}
            >
              {smsBusy ? '…' : 'Confirm'}
            </button>
          </div>
          <div className="ts-hint" style={{ marginTop: 4 }}>
            By confirming you consent to receive critical-alert texts. Message + data rates may
            apply. Reply STOP to opt out anytime.
          </div>
        </div>
      )}
      {smsOn && smsNote && (
        <div className="ts-hint" style={{ paddingLeft: 26, color: 'var(--cyan)' }}>
          {smsNote}
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, disabled, label, hint, onChange }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '6px 0',
        cursor: disabled ? 'wait' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, color: 'var(--text)' }}>{label}</div>
        <div className="ts-hint">{hint}</div>
      </div>
    </label>
  );
}

// Preferred reorder retailer — the "Reorder →" buttons in Today's
// Plan + each part row resolve their URL through this. NULL means
// "no preference" (resolver uses the first retailer in the library,
// shipping as BRS).
function RetailerPreferenceRow({ me, onChanged }) {
  const config = useConfig();
  const retailers = config?.reorder?.retailers || [];
  const current = me?.preferred_retailer || '';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!retailers.length) return null;

  const onPick = async (k) => {
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/me', {
        method: 'PATCH',
        body: { preferred_retailer: k || null },
      });
      await onChanged();
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
          marginBottom: 6,
        }}
      >
        Preferred retailer
      </div>
      <select value={current} onChange={(e) => onPick(e.target.value)} disabled={busy}>
        <option value="">No preference</option>
        {retailers.map((r) => (
          <option key={r.k} value={r.k}>
            {r.label}
          </option>
        ))}
      </select>
      <p className="ts-hint" style={{ marginTop: 6 }}>
        The store the Reorder links open for supplies running low and equipment parts. With no
        preference they open {retailers[0]?.label || 'the first store in the list'}.
      </p>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
    </div>
  );
}

function EmailChangeForm({ currentEmail, onDone }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(null);

  const submit = async () => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setErr('Enter a valid email address.');
      return;
    }
    if (trimmed === currentEmail.toLowerCase()) {
      setErr('That is already your email.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const r = await api('/api/auth/request-email-change', {
        method: 'POST',
        body: { new_email: trimmed },
      });
      // The server answers sent:false when the email did not go out; "Sent
      // a confirmation link" then sent the keeper to an empty inbox.
      if (r?.sent === false && !r.devUrl) {
        setErr("We couldn't send the email just now. Try again in a minute.");
        return;
      }
      setSent(trimmed);
    } catch (e) {
      setErr(e.payload?.error || e.message || 'Could not send the confirmation email.');
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <>
        <p className="ts-hint" style={{ marginTop: 8 }}>
          Sent a confirmation link to <strong>{sent}</strong>. Open that inbox and click the link to
          finish the change — your current email keeps working until you do.
        </p>
        <div className="ts-row-body" style={{ marginTop: 8 }}>
          <button type="button" className="ts-chip" onClick={onDone}>
            Done
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <label className="ts-field" style={{ marginTop: 8 }}>
        <span>New email</span>
        <input
          type="email"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="you@example.com"
        />
      </label>
      {err && (
        <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
          {err}
        </div>
      )}
      <div className="ts-row-body" style={{ marginTop: 8 }}>
        <button type="button" className="ts-save" onClick={submit} disabled={busy || !value.trim()}>
          {busy ? '…' : 'Send confirmation'}
        </button>
        <button type="button" className="ts-chip" onClick={onDone}>
          Cancel
        </button>
      </div>
    </>
  );
}

function PasswordForm({ hasPassword, onDone }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  const submit = async () => {
    if (next.length < 8) {
      setErr('Use at least 8 characters.');
      return;
    }
    if (hasPassword && !current) {
      setErr('Enter your current password.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/set-password', {
        method: 'POST',
        body: hasPassword ? { password: next, current_password: current } : { password: next },
      });
      setSaved(true);
    } catch (e) {
      setErr(e.payload?.error || e.message || 'Could not save your password.');
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <>
        <p className="ts-hint" style={{ marginTop: 8 }}>
          Password updated. Other devices have been signed out.
        </p>
        <div className="ts-row-body" style={{ marginTop: 8 }}>
          <button type="button" className="ts-chip" onClick={onDone}>
            Done
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {hasPassword && (
        <label className="ts-field" style={{ marginTop: 8 }}>
          <span>Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
      )}
      <label className="ts-field" style={{ marginTop: 8 }}>
        <span>{hasPassword ? 'New password' : 'New password (8+ characters)'}</span>
        <input
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
      </label>
      {err && (
        <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
          {err}
        </div>
      )}
      <div className="ts-row-body" style={{ marginTop: 8 }}>
        <button type="button" className="ts-save" onClick={submit} disabled={busy || !next}>
          {busy ? '…' : hasPassword ? 'Change password' : 'Set password'}
        </button>
        <button type="button" className="ts-chip" onClick={onDone}>
          Cancel
        </button>
      </div>
    </>
  );
}

// Its own card at the very bottom of the Account sheet, away from the
// invite link and everything else a keeper taps often.
export function DeleteAccountSection() {
  const [email, setEmail] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api('/api/auth/me')
      .then((r) => setEmail(r.user?.email || null))
      .catch(() => {});
  }, []);
  if (!email) return null;
  return (
    <div className="ts-add-form" style={{ marginTop: 12 }}>
      {open ? (
        <DeleteAccountForm email={email} onCancel={() => setOpen(false)} />
      ) : (
        <div className="ts-row-body">
          <button
            type="button"
            className="ts-chip"
            style={{ color: 'var(--alert)' }}
            onClick={() => setOpen(true)}
          >
            Delete account
          </button>
        </div>
      )}
    </div>
  );
}

// Deleting the account removes every tank and everything in it. Typing
// the email is the confirmation, since email-link accounts have no
// password to re-enter.
function DeleteAccountForm({ email, onCancel }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const matches = typed.trim().toLowerCase() === String(email || '').toLowerCase();

  const submit = async () => {
    if (!matches) return;
    setBusy(true);
    setErr('');
    try {
      await api('/api/account/delete', { method: 'POST', body: { confirm_email: typed.trim() } });
      window.location.href = '/';
    } catch (e) {
      setErr(e.payload?.error || e.message || 'Could not delete the account.');
      setBusy(false);
    }
  };

  return (
    <>
      <p className="ts-hint" style={{ marginTop: 8 }}>
        This deletes your account and every tank in it: readings, ICP results and their PDFs,
        photos, tasks, supplies, livestock and settings. People you shared a tank with lose it too.
        It cannot be undone. Export a tank to Excel from its settings first if you want a copy.
      </p>
      <label className="ts-field" style={{ marginTop: 8 }}>
        <span>Type {email} to confirm</span>
        <input
          type="email"
          autoComplete="off"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </label>
      {err && (
        <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
          {err}
        </div>
      )}
      <div className="ts-row-body" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="ts-save"
          style={{ background: 'var(--alert)' }}
          onClick={submit}
          disabled={busy || !matches}
        >
          {busy ? '…' : 'Delete my account'}
        </button>
        <button type="button" className="ts-chip" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </>
  );
}
